import { RefObject, useEffect, useMemo } from 'react';
import { Badge, Flex } from 'antd';
import {
  ContentSegments,
  DataLoadingType,
  EmptyData,
  FormRef,
  GroupDescriptionType,
  InputDataType,
  SampleCommentType,
} from '@atlas-frontend/sdk';
import {
  TableFilterSortProps,
  InputDataViewTable,
} from '../table/InputDataViewTable';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './InputDataDetailsPanel.translations.json';
import {
  useInputDataDetailsStoreValue,
  useSetInputDataDetailsStoreValue,
} from './InputDataDetailsContext';
import styled from 'styled-components';
import { InputDataEditTable } from '../table/InputDataEditTable';
import { inputEditError } from './inputErrorUtil';
import { SampleCommentsEditTable } from '../sample/SampleCommentsEditTable';
import { SampleCommentsViewTable } from '../sample/SampleCommentsViewTable';
import { InputDataVersionType } from './types';

const { addTranslations, useTranslation } = i18nNsFactory(
  'input-data-details-panel',
);
addTranslations(translations);

export interface InputDataDetailsEditPanelProps {
  data?: InputDataType;
  comments: SampleCommentType[];
  date: string;
  groupId?: number;
  description?: GroupDescriptionType;
  descriptions: GroupDescriptionType[];
  sampleId?: number;
  loadingStatus?: DataLoadingType;
  subject: string;
  version?: InputDataVersionType;
  formRef?: RefObject<FormRef<any> | null>;
  isLoading: boolean;
  onAddComment: (comment: SampleCommentType) => void;
  tableProps?: TableFilterSortProps;
  subjectNameMappings?: Record<string, string>;
}

export function InputDataDetailsPanel(props: InputDataDetailsEditPanelProps) {
  const { t } = useTranslation();
  const { loadingStatus } = props;

  if (loadingStatus === 'NO_OUTPUT_SELECTED') {
    return <EmptyData>{t(`output.${loadingStatus}`)}</EmptyData>;
  }
  if (loadingStatus === 'NO_SAMPLE') {
    return <EmptyData>{t(`input.${loadingStatus}`)}</EmptyData>;
  }

  return <InputDataDetailsPanelInner {...props} />;
}

function InputDataDetailsPanelInner({
  data,
  date,
  description,
  descriptions,
  comments,
  sampleId,
  subject,
  loadingStatus,
  groupId,
  formRef,
  version,
  isLoading,
  onAddComment,
  tableProps,
  subjectNameMappings,
}: InputDataDetailsEditPanelProps) {
  const { t } = useTranslation();
  const [mode] = useInputDataDetailsStoreValue('mode');
  const [, setSegment] = useInputDataDetailsStoreValue('segment');
  const setRowsTotal = useSetInputDataDetailsStoreValue('total');
  const setCommentsTotal = useSetInputDataDetailsStoreValue('commentsTotal');

  const inputError = useMemo(() => {
    if (version === 'output') {
      return undefined;
    }
    const inputError = inputEditError(
      loadingStatus,
      description?.activeVersionId,
      data?.columns?.length,
    );
    if (inputError) {
      return t(`input.${inputError}`);
    }

    return undefined;
  }, [
    data?.columns?.length,
    description?.activeVersionId,
    loadingStatus,
    t,
    version,
  ]);

  const InputDataTableComponent = useMemo(
    () => (mode === 'edit' ? InputDataEditTable : InputDataViewTable),
    [mode],
  );
  const SampleCommentsTableComponent = useMemo(
    () => (mode === 'edit' ? SampleCommentsEditTable : SampleCommentsViewTable),
    [mode],
  );

  useEffect(() => {
    setRowsTotal(data?.rows?.length ?? 0);
    setCommentsTotal(comments.length);
  }, [comments, data, setCommentsTotal, setRowsTotal]);

  const segments = useMemo(() => {
    return [
      {
        value: 'data',
        label: (
          <Flex gap="small" align="center">
            {t('segment.data')}
            <StoreCountBadge storeKey="total" />
          </Flex>
        ),
        children: inputError ? (
          <EmptyData>{inputError}</EmptyData>
        ) : (
          <TableWrapper vertical gap="middle">
            <InputDataTableComponent
              data={data}
              date={date}
              descriptionId={description?.id ?? undefined}
              isDetailed
              subject={subject}
              isLoading={isLoading}
              comments={comments}
              descriptions={descriptions}
              sampleId={sampleId}
              groupId={groupId}
              onAddComment={onAddComment}
              stateStorageKey="inputDataDetailsTable"
              tableProps={tableProps}
              subjectNameMappings={subjectNameMappings}
            />
          </TableWrapper>
        ),
      },
      {
        value: 'comments',
        label: (
          <Flex gap="small" align="center">
            {t('segment.comments')}
            <StoreCountBadge storeKey="commentsTotal" />
          </Flex>
        ),
        children: sampleId && (
          <SampleCommentsTableComponent
            comments={comments}
            descriptions={descriptions}
            descriptionId={description?.id ?? undefined}
            sampleId={sampleId}
            groupId={groupId}
            isLoading={isLoading}
          />
        ),
      },
    ];
  }, [
    t,
    inputError,
    InputDataTableComponent,
    data,
    date,
    description?.id,
    subject,
    isLoading,
    comments,
    descriptions,
    sampleId,
    groupId,
    onAddComment,
    tableProps,
    subjectNameMappings,
    SampleCommentsTableComponent,
  ]);

  return (
    <ContentSegments
      segments={segments}
      onChangeSegment={(value) => setSegment(value)}
      shouldBlock={() => formRef?.current?.isBlocked ?? false}
      onSupressBlock={() => {
        formRef?.current?.form.reset(undefined, {
          keepValues: false,
          keepDirty: false,
        });
        formRef?.current?.supressBlock();
      }}
    />
  );
}

const StoreCountBadge = ({
  storeKey,
}: {
  storeKey: 'total' | 'commentsTotal';
}) => {
  const [count] = useInputDataDetailsStoreValue(storeKey);
  return <Badge count={count} />;
};

const TableWrapper = styled(Flex)`
  width: 100%;
  height: 100%;
  margin-right: auto;
  margin-left: auto;
`;
